// ─── Daftar asal feed geser (`?dari=`) ──────────────────────────────────────
//
// Pembeli yang membuka produk dari sebuah DAFTAR — halaman merek, segmen,
// rail kurasi, PO Segera Ditutup, promo, hasil pencarian — menggeser feed
// halaman produk DI DALAM daftar itu dulu, dengan urutan daftar itu. Daftar
// habis → feed kategori biasa (`productFeed.ts`), dan param ini dilepas.
//
// Halaman kategori SENGAJA tak punya konteks: feed kategori sudah persis
// "habiskan kategori ini dulu, lalu kategori berikutnya".
//
// Nilainya datang dari URL (bisa diketik siapa pun), jadi parser ini ketat:
// jenis dari daftar putih, nilai dipangkas & dibatasi panjangnya, karakter
// kontrol ditolak. Nilai yang lolos hanya dipakai MENYARING data yang sudah
// ter-scope toko di server — tak pernah disusun jadi query mentah.

export const FEED_CONTEXT_PARAM = "dari";

const MAX_VALUE_LENGTH = 80;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

export type FeedContext =
  | { kind: "segmen"; value: string }
  | { kind: "merek"; value: string }
  | { kind: "rail"; value: string }
  | { kind: "segera" }
  | { kind: "promo"; value: string | null }
  | { kind: "cari"; value: string };

/** Nilai `?dari=` → konteks; apa pun yang tak dikenal → `null` (feed kategori). */
export function parseFeedContext(
  raw: string | readonly string[] | null | undefined
): FeedContext | null {
  const text = (typeof raw === "string" ? raw : raw?.[0])?.trim();
  if (!text) return null;
  const colon = text.indexOf(":");
  const kind = (colon === -1 ? text : text.slice(0, colon)).trim().toLowerCase();
  const value = colon === -1 ? "" : text.slice(colon + 1).trim();
  if (value.length > MAX_VALUE_LENGTH || CONTROL_CHARS.test(value)) return null;
  switch (kind) {
    case "segera":
      return value ? null : { kind: "segera" };
    case "promo":
      return { kind: "promo", value: value || null };
    case "segmen":
    case "merek":
    case "rail":
    case "cari":
      return value ? { kind, value } : null;
    default:
      return null;
  }
}

export function formatFeedContext(context: FeedContext): string {
  switch (context.kind) {
    case "segera":
      return "segera";
    case "promo":
      return context.value ? `promo:${context.value}` : "promo";
    default:
      return `${context.kind}:${context.value}`;
  }
}

/**
 * Konteks untuk kartu di `CatalogBrowse` (halaman merek, segmen, pencarian
 * katalog reseller): kata kunci menang, lalu merek yang dikunci, lalu segmen
 * yang dikunci TANPA kategori. Kategori (dengan/tanpa segmen) → `null`: feed
 * kategori sudah persis "kategori ini dulu". Kata kunci kepanjangan → `null`
 * (lebih baik feed kategori daripada daftar hasil kueri yang terpotong).
 */
export function browseFeedContext(dims: {
  query?: string | null;
  lockedBrand?: string | null;
  lockedCategory?: string | null;
  lockedSegment?: string | null;
}): FeedContext | null {
  const query = dims.query?.trim();
  if (query) return query.length <= MAX_VALUE_LENGTH ? { kind: "cari", value: query } : null;
  const brand = dims.lockedBrand?.trim();
  if (brand) return { kind: "merek", value: brand };
  const segment = dims.lockedSegment?.trim();
  if (segment && !dims.lockedCategory?.trim()) return { kind: "segmen", value: segment };
  return null;
}

/**
 * Tempelkan konteks ke tautan produk. Titik dua dibiarkan terbaca
 * (`?dari=merek:Galea%20Kids`) — sah di query string dan lebih mudah dibaca
 * di URL yang dibagikan daripada `%3A`.
 */
export function withFeedContext(
  href: string,
  context: FeedContext | null | undefined
): string {
  if (!context) return href;
  const value = encodeURIComponent(formatFeedContext(context)).replace(/%3A/gi, ":");
  return `${href}${href.includes("?") ? "&" : "?"}${FEED_CONTEXT_PARAM}=${value}`;
}
